import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { faEnvelope, faPhone, faPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { message, Upload } from "antd";
import EditableText from "@/components/EditableText";
import ShowActionButtons from "Pages/DigitalProfile/ShowActionButtons";
import UploadProfilePic from "@/components/UploadProfilePic";
import { updateAccountDetails } from "Sdk/Actions";
import {
  getThemeColor,
  getUserDetailsFromReducer,
  updateUserDetailsInReducer,
} from "Store/reducers/Auth/AuthSlice";
import { uploadServicesPic } from "Utility/UploadFile";
import ShowSocialMediaIcons from "./ShowSocialMediaIcons";

// Debounce function to limit the rate of updates
const debounce = (func, delay) => {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), delay);
  };
};

export const Profile = () => {
  const userDetails = useSelector(getUserDetailsFromReducer);
  const themeColorString = useSelector(getThemeColor);
  let themeColor;
  const dispatch = useDispatch();

  // Debounced version of database update
  const debouncedUpdateAccountDetails = useCallback(
    debounce(async (userData) => {
      await updateAccountDetails(userData);
      message.success("Changes saved! Publish to go live.");
    }, 1000), // Adjust the delay as needed
    [updateAccountDetails]
  );

  const UpdateProfileData = (values) => {
    const userData = {
      ...values,
      username: userDetails?.username,
    };
    // Immediate Redux update
    dispatch(updateUserDetailsInReducer(userData));
    // Debounced database update
    debouncedUpdateAccountDetails(userData);
  };

  try {
    themeColor = JSON.parse(themeColorString);
  } catch (error) {
    console.error(
      "Failed to parse themeColor in Profile:",
      themeColorString,
      error
    );
    themeColor = {
      background: "#E6E6E6",
      text: "#333C4D",
      btnBg: "#ffffff",
      btnText: "#333C4D",
    };
  }

  const [ProfilePicFile, setProfilePicFile] = useState(null);
  const [imagePreview, setimagePreview] = useState(null);

  const setPreviewAndUpload = async (file) => {
    setimagePreview(await getBase64(file));
    const uploadResult = await uploadServicesPic(file, userDetails?.username);
    UpdateProfileData({ logo: uploadResult?.key });
  };

  const props = {
    name: "file",
    multiple: false,
    async onChange(info) {
      setProfilePicFile(info?.fileList?.[0]?.originFileObj);
    },
    onDrop(e) {
      setProfilePicFile(e.dataTransfer.files?.[0]?.originFileObj);
    },
    beforeUpload() {
      return false;
    },
    maxCount: 1,
    accept: "image/*",
  };

  useEffect(() => {
    if (ProfilePicFile) {
      setPreviewAndUpload(ProfilePicFile);
    } else {
      setimagePreview(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ProfilePicFile]);

  const getBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  return (
    <div className="flex flex-col items-center lg:items-start w-full h-full">
      {/* Profile Photo */}
      <div className="mb-6 lg:mb-8">
        <Upload.Dragger
          {...props}
          className={`min-h-[100px] min-w-[100px] sm:min-h-[120px] sm:min-w-[120px] md:min-h-[150px] md:min-w-[150px] lg:min-h-[180px] lg:min-w-[180px] w-full h-full flex items-center justify-center transition duration-500 border-dashed`}
          style={{
            borderRadius: "50%",
            borderColor:
              imagePreview && userDetails?.logo
                ? themeColor.text
                : "transparent",
          }}
          showUploadList={false}
          listType="picture-circle"
        >
          {userDetails?.logo || imagePreview ? (
            <UploadProfilePic
              S3Key={userDetails?.logo}
              src={imagePreview}
              alt="User Logo"
              width={180}
              height={180}
            />
          ) : (
            <p
              className="text-sm sm:text-base lg:text-lg"
              style={{ color: themeColor?.text }}
            >
              <FontAwesomeIcon icon={faPlus} />
              <br />
              Add Logo
            </p>
          )}
        </Upload.Dragger>
      </div>

      {/* Profile Information */}
      <div className="w-full text-center lg:text-left">
        <EditableText
          className="font-bold w-full text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl"
          onEditDone={UpdateProfileData}
          placeholder="Your Business Name"
          name="name"
          key="name"
        >
          {userDetails?.name}
        </EditableText>
        <EditableText
          className="w-full text-sm sm:text-base md:text-lg lg:text-xl xl:text-2xl mt-2 lg:mt-4"
          onEditDone={UpdateProfileData}
          name="description"
          placeholder="Your Bio..."
          key="description"
        >
          {userDetails?.description}
        </EditableText>
        <EditableText
          className="text-sm sm:text-base lg:text-lg w-full text-center lg:text-left p-0 mt-3 lg:mt-4"
          onEditDone={UpdateProfileData}
          name="phone"
          key="phone"
          placeholder="Phone Number"
          icon={<FontAwesomeIcon icon={faPhone} />}
        >
          {userDetails?.phone}
        </EditableText>
        <EditableText
          className="text-sm sm:text-base lg:text-lg w-full text-center lg:text-left p-0 mt-2 lg:mt-3"
          onEditDone={UpdateProfileData}
          name="email"
          key="email"
          placeholder="Email"
          icon={<FontAwesomeIcon icon={faEnvelope} />}
        >
          {userDetails?.email}
        </EditableText>
      </div>

      {/* Show profile links */}
      <div className="my-4 lg:my-6 w-full">
        <ShowSocialMediaIcons />
        <div className="w-full flex flex-col justify-center items-center lg:items-start">
          <ShowActionButtons />
        </div>
      </div>
    </div>
  );
};
